import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';

import '../styles/DeferredFooterLoader.css';

const Footer = lazy(() => import('./Footer'));

const DeferredFooterLoader = ({ rootMargin = '700px 0px' }) => {
  const sentinelRef = useRef(null);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (isActive) return undefined;

    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    let idleId = null;
    let timerId = null;
    let cancelled = false;

    const activate = () => {
      if (!cancelled) setIsActive(true);
    };

    const activateWhenBrowserIsFree = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(activate, { timeout: 1600 });
      } else {
        timerId = window.setTimeout(activate, 500);
      }
    };

    if (!('IntersectionObserver' in window)) {
      activateWhenBrowserIsFree();
      return () => {
        cancelled = true;
        if (timerId !== null) window.clearTimeout(timerId);
        if (
          idleId !== null &&
          typeof window.cancelIdleCallback === 'function'
        ) {
          window.cancelIdleCallback(idleId);
        }
      };
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        activateWhenBrowserIsFree();
      },
      {
        root: null,
        rootMargin,
        threshold: 0.01,
      }
    );

    observer.observe(sentinel);

    return () => {
      cancelled = true;
      observer.disconnect();
      if (timerId !== null) window.clearTimeout(timerId);
      if (
        idleId !== null &&
        typeof window.cancelIdleCallback === 'function'
      ) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [isActive, rootMargin]);

  return (
    <div ref={sentinelRef} className="deferred-footer-loader">
      {isActive && (
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      )}
    </div>
  );
};

export default DeferredFooterLoader;
